import { Popper } from "@material-ui/core";
import { Tooltip } from "@material-ui/core";
import classnames from "classnames";
import { History } from "history";
import { contains, map } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import api from "src/api";
import {
  getComments as getCommentsAction,
  GetCommentsActionType
} from "src/commentView/actions";
import { CommentIcon } from "src/commentView/CommentIcon";
import { CommentPopover } from "src/commentView/CommentPopover";
import { Comment } from "src/commentView/types";
import { Entity } from "src/documentUpload/types";
import { getEntityViewState } from "src/entityView/selector";
import { User } from "src/login/types";
import QuiqButton from "src/shared/QuiqButton";
import { QuiqIcon } from "src/shared/QuiqIcon";
import selectorsToProps from "src/utils/selectorsToProps";
import { reloadBriefSpreads, ReloadBriefSpreadsActionType } from "./actions";
import DocumentStatus from "./DocumentStatus";
import { FlagIcon } from "./FlagIcon";
import {
  getHasUnreadComments,
  isDeletableState,
  isDisabledState,
  isSuccessful
} from "./helpers";
import { LockIcon } from "./LockIcon";
import MoreOptions from "./MoreOptions";
import {
  BriefSpread,
  HUMAN_READABLE_FIXED_STATUSES,
  SPREAD_STATUSES
} from "./types";
import { UnlockedIcon } from "./UnlockedIcon";

import { getComments } from "../spread/model/spread/selector";

interface DocumentsListRowProps {
  document: BriefSpread;
  history: History;
  user: User;
  reloadBriefSpreads: ReloadBriefSpreadsActionType;
  entity: Entity | null;
  getComments: GetCommentsActionType;
  hasComments?: boolean;
  comments: Comment[];
}

interface DocumentsListRowState {
  lockIconElement: (EventTarget & Element) | null;
  latestPeriodElement: (EventTarget & Element) | null;
  isUnlockingByButton: boolean;
  isUnlockedByButton: boolean;
  comments: Comment[] | null;
  commentsPopoverAnchorEl: HTMLElement | null;
  areCommentsLoading: boolean;
  hasUnreadComments: boolean;
  entityNameEl: HTMLElement | null;
  modifiedByUserEl: HTMLElement | null;
  modificationTimeEl: HTMLElement | null;
  uploadedByUserEl: HTMLElement | null;
  uploadTimeEl: HTMLElement | null;
  toggleCommentPopup: boolean;
}

export class DocumentsListRow extends React.Component<
  DocumentsListRowProps,
  DocumentsListRowState
> {
  constructor(props: DocumentsListRowProps) {
    super(props);

    this.state = {
      lockIconElement: null,
      latestPeriodElement: null,
      isUnlockingByButton: false,
      isUnlockedByButton: false,
      comments: null,
      commentsPopoverAnchorEl: null,
      areCommentsLoading: false,
      hasUnreadComments: getHasUnreadComments(props.document, props.user),
      entityNameEl: null,
      modifiedByUserEl: null,
      modificationTimeEl: null,
      uploadedByUserEl: null,
      uploadTimeEl: null,
      toggleCommentPopup: false
    };
  }

  public componentDidUpdate(prevProps) {
    if (this.props.comments !== prevProps.comments) {
      this.setState({
        comments: this.props.comments,
        areCommentsLoading: false
      });
    }
  }

  public viewSpread = (
    spreadId: string,
    environmentName: string,
    status: string
  ) => {
    if (this.isAvailable()) {
      this.props.history.push(
        `/spreads/${spreadId}/${environmentName}/${status}`
      );
    }
  };

  public clickLock = (event: React.SyntheticEvent) => {
    const { currentTarget } = event;

    this.setState({
      lockIconElement: this.state.lockIconElement ? null : currentTarget
    });
  };

  public clickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    const { spreadId, environmentName, status } = this.props.document;
    return this.viewSpread(spreadId, environmentName, status);
  };

  public isAvailable = (): boolean => {
    return !this.isDisabled() && this.isClickable();
  };

  public isClickable = (): boolean => {
    if (this.props.document.isAccessible !== undefined) {
      return this.props.document.isAccessible;
    }

    if (this.isLockedBySomeoneElse()) {
      return false;
    }

    const { status } = this.props.document;
    const { hasManagedService, isBankAnalyst } = this.props.user;

    if (hasManagedService) {
      if (
        (isBankAnalyst && status === SPREAD_STATUSES.PENDING_REVIEW) ||
        (!isBankAnalyst && status === SPREAD_STATUSES.FINAL_REVIEW_COMPLETED)
      ) {
        return false;
      }
    }
    return isSuccessful(status);
  };

  public isDisabled = () => isDisabledState(this.props.document.status);

  public isDeletable = () => {
    const { status } = this.props.document;
    if (this.isLockedBySomeoneElse() || status === SPREAD_STATUSES.REJECTED) {
      return false;
    }

    if (this.props.document.isAccessible !== undefined) {
      return this.props.document.isAccessible;
    }
    const { hasManagedService, isBankAnalyst } = this.props.user;

    if (hasManagedService) {
      if (
        (!isBankAnalyst && status === SPREAD_STATUSES.PENDING_FINAL_REVIEW) ||
        (!isBankAnalyst && status === SPREAD_STATUSES.FINAL_REVIEW_COMPLETED)
      ) {
        return false;
      }
    }

    return isDeletableState(this.props.document.status);
  };

  public areMoreOptionsAvailable = () => {
    return this.isDeletable();
  };

  public handleCommentClick = (event?: React.MouseEvent<HTMLElement>) => {
    const isCommentPopoverOpen = Boolean(this.state.commentsPopoverAnchorEl);

    if (event) {
      event.stopPropagation();
      this.setState({
        commentsPopoverAnchorEl: isCommentPopoverOpen
          ? null
          : event.currentTarget
      });
    }
    if (!isCommentPopoverOpen) {
      this.fetchComments();
    }
  };

  public handlePopoverClose = (event?: React.SyntheticEvent<{}>) => {
    if (this.state.hasUnreadComments && this.props.document.commentsReadBy) {
      this.markCommentsAsRead();
    }
    this.setState({ commentsPopoverAnchorEl: null });
    this.setState({ toggleCommentPopup: false });
  };

  public mailToMoodysSupport = (event?: React.MouseEvent<HTMLElement>) => {
    if (event) {
      event.stopPropagation();
      window.location.href = "mailto:MA_Techsupport@moodys.com";
    }
  };

  public renderSpreadListView = () => {
    const {
      documentName,
      modifiedByUser,
      modificationTime,
      status,
      locked,
      lockedBy,
      rejected,
      priority,
      entityName,
      latestPeriod,
      modelTemplateType,
      timestamp,
      humanReadableStatus,
      uploadedByUser
    } = this.props.document;

    const { hasManagedService } = this.props.user;
    const {
      entityNameEl,
      modifiedByUserEl,
      modificationTimeEl,
      uploadedByUserEl,
      uploadTimeEl
    } = this.state;

    const isModelError =
      humanReadableStatus === HUMAN_READABLE_FIXED_STATUSES.MODEL_ERROR;
    return (
      <div
        id={`row-${this.props.document.spreadId}`}
        onClick={this.clickHandler}
        className={classnames("Documents__row Documents__row--spread-list", {
          "Documents__row--disabled": this.isDisabled(),
          "Documents__row--notClickable": !this.isClickable()
        })}
      >
        <div className="Documents__column--col-1 Documents__row--priority">
          {priority ? <FlagIcon /> : ""}
        </div>
        <div
          className={classnames(
            "Documents__row--name",
            "Documents__column--col-2",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
          onMouseOver={this.onEntityNameMouseOver}
          onMouseOut={this.onEntityNameMouseOut}
        >
          <Popper
            id={"id"}
            open={this.state.entityNameEl !== null}
            anchorEl={this.state.entityNameEl}
            placement="top"
          >
            <div className={"Popper"}>{entityName || documentName}</div>
          </Popper>
          {entityName || documentName}
          {entityNameEl && this.renderFullText(entityNameEl)}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-3",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          <Popper
            id={"id"}
            open={this.state.latestPeriodElement !== null}
            anchorEl={this.state.latestPeriodElement}
            placement="top"
          >
            <div className={"Popper"}>{this.renderPeriodCount()}</div>
          </Popper>
          <div
            className="Documents__row__latestPeriod"
            onMouseOver={this.onLatestPeriodMouseOver}
            onMouseOut={this.onLatestPeriodMouseOut}
          >
            {latestPeriod ? latestPeriod : "No Date"}
          </div>
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-4",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {modelTemplateType}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-5",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
          onMouseOver={this.onModifiedByUserMouseOver}
          onMouseOut={this.onModifiedByUserMouseOut}
        >
          {modifiedByUser}
          {modifiedByUserEl && this.renderFullText(modifiedByUserEl)}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEdited",
            "Documents__column--col-6",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
          onMouseOver={this.onModificationTimeMouseOver}
          onMouseOut={this.onModificationTimeMouseOut}
        >
          {modificationTime}
          {modificationTimeEl && this.renderFullText(modificationTimeEl)}
        </div>
        <div
          className={classnames(
            "Documents__row--uploadedBy",
            "Documents__column--col-7",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
          onMouseOver={this.onUploadedByUserMouseOver}
          onMouseOut={this.onUploadedByUserMouseOut}
        >
          {uploadedByUser}
          {uploadedByUserEl && this.renderFullText(uploadedByUserEl)}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEdited",
            "Documents__column--col-8",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
          onMouseOver={this.onUploadTimeMouseOver}
          onMouseOut={this.onUploadTimeMouseOut}
        >
          {timestamp}
          {uploadTimeEl && this.renderFullText(uploadTimeEl)}
        </div>
        <div
          className={classnames("Documents__column--col-9", "Documents__lock", {
            Documents__row__value__disabled: this.isDisabled()
          })}
        >
          {this.lockIcon(locked, lockedBy)}
        </div>
        <div
          className={classnames("Documents__column--col-10", {
            Documents__row__value__disabled: this.isDisabled()
          })}
        >
          <div
            {...(isModelError && { onClick: this.mailToMoodysSupport })}
            className={classnames("Documents__status", {
              "Documents__row--value-notClickable": !this.isClickable(),
              "Documents__row--value-disabled": this.isDisabled(),
              Documents__pointer: isModelError
            })}
          >
            <DocumentStatus
              hasManagedService={hasManagedService}
              status={status}
              isLocked={locked}
              rejected={rejected}
              humanReadableStatus={humanReadableStatus}
            />
          </div>
        </div>
        {
          <div className="Documents__column--col-11">
            {this.props.hasComments !== false && (
              <QuiqIcon
                className={classnames("DocumentsListRow__comment-button", {
                  "DocumentsListRow__comment-button--open": Boolean(
                    this.state.commentsPopoverAnchorEl
                  )
                })}
                color={"inherit"}
                onClick={this.handleCommentClick}
                isLoading={this.state.areCommentsLoading}
              >
                <React.Fragment>
                  <div
                    className={classnames(
                      "DocumentsListRow__read-status",
                      {
                        "DocumentsListRow__read-status--unread": this.state
                          .hasUnreadComments
                      },
                      {
                        "DocumentsListRow__read-status--read": !this.state
                          .hasUnreadComments
                      }
                    )}
                  />
                  <CommentIcon />
                </React.Fragment>
              </QuiqIcon>
            )}
            <CommentPopover
              comments={this.state.comments}
              shouldHideInput={true}
              isOpen={
                Boolean(this.state.commentsPopoverAnchorEl) &&
                !this.state.areCommentsLoading &&
                this.state.toggleCommentPopup
              }
              spreadId={this.props.document.spreadId}
              onClose={this.handlePopoverClose}
              anchorEl={this.state.commentsPopoverAnchorEl}
              updateComments={this.updateComments}
            />
          </div>
        }
        <div className="Documents__column--col-12">
          {this.areMoreOptionsAvailable() && (
            <MoreOptions
              document={this.props.document}
              isDeletable={this.isDeletable()}
            />
          )}
        </div>
      </div>
    );
  };
  public renderListInEntityView = () => {
    const {
      modifiedByUser,
      modificationTime,
      status,
      locked,
      lockedBy,
      rejected,
      priority,
      latestPeriod,
      modelTemplateType,
      timestamp,
      humanReadableStatus,
      uploadedByUser
    } = this.props.document;

    const { hasManagedService } = this.props.user;

    return (
      <div
        id={`row-${this.props.document.spreadId}`}
        onClick={this.clickHandler}
        className={classnames("Documents__row  Documents__row--entity-view", {
          "Documents__row--disabled": this.isDisabled(),
          "Documents__row--notClickable": !this.isClickable()
        })}
      >
        <div className="Documents__column--col-1 Documents__row--priority">
          {priority ? <FlagIcon /> : ""}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-2",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          <Popper
            id={"id"}
            open={this.state.latestPeriodElement !== null}
            anchorEl={this.state.latestPeriodElement}
            placement="top"
          >
            <div className={"Popper"}>{this.renderPeriodCount()}</div>
          </Popper>
          <div
            className="Documents__row__latestPeriod"
            onMouseOver={this.onLatestPeriodMouseOver}
            onMouseOut={this.onLatestPeriodMouseOut}
          >
            {latestPeriod ? latestPeriod : "No Date"}
          </div>
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-3",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {modelTemplateType}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEditedBy",
            "Documents__column--col-4",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {modifiedByUser}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEdited",
            "Documents__column--col-5",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {modificationTime}
        </div>
        <div
          className={classnames(
            "Documents__row--uploadedBy",
            "Documents__column--col-6",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {uploadedByUser}
        </div>
        <div
          className={classnames(
            "Documents__row--lastEdited",
            "Documents__column--col-7",
            {
              "Documents__row--value-disabled": this.isDisabled(),
              "Documents__row--value-notClickable": !this.isClickable()
            }
          )}
        >
          {timestamp}
        </div>

        <div
          className={classnames("Documents__column--col-8", "Documents__lock", {
            Documents__row__value__disabled: this.isDisabled()
          })}
        >
          {this.lockIcon(locked, lockedBy)}
        </div>
        <div
          className={classnames("Documents__column--col-9", {
            Documents__row__value__disabled: this.isDisabled()
          })}
        >
          <div
            className={classnames("Documents__status", {
              "Documents__row--value-notClickable": !this.isClickable(),
              "Documents__row--value-disabled": this.isDisabled()
            })}
          >
            <DocumentStatus
              hasManagedService={hasManagedService}
              status={status}
              humanReadableStatus={humanReadableStatus}
              isLocked={locked}
              rejected={rejected}
            />
          </div>
        </div>
        {this.props.document.commentsReadBy && (
          <div className="Documents__column--col-11">
            <QuiqIcon
              className={classnames("DocumentsListRow__comment-button", {
                "DocumentsListRow__comment-button--open": Boolean(
                  this.state.commentsPopoverAnchorEl
                )
              })}
              children={<CommentIcon />}
              color={"inherit"}
              onClick={this.handleCommentClick}
            />
            <CommentPopover
              comments={this.state.comments}
              shouldHideInput={true}
              isOpen={
                Boolean(this.state.commentsPopoverAnchorEl) &&
                this.state.toggleCommentPopup
              }
              spreadId={this.props.document.spreadId}
              onClose={this.handlePopoverClose}
              anchorEl={this.state.commentsPopoverAnchorEl}
              updateComments={this.updateComments}
            />
          </div>
        )}
        <div className="Documents__column--col-12">
          {this.areMoreOptionsAvailable() && (
            <MoreOptions
              document={this.props.document}
              isDeletable={this.isDeletable()}
            />
          )}
        </div>
      </div>
    );
  };

  public render() {
    const isNestedInEntityView = this.props.entity !== null;

    return isNestedInEntityView
      ? this.renderListInEntityView()
      : this.renderSpreadListView();
  }

  public changeUnlockButtonStatus = (
    isUnlockingByButton: boolean,
    isUnlockedByButton: boolean
  ) => {
    this.setState({
      isUnlockingByButton,
      isUnlockedByButton
    });
  };

  public unlockSpread = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const { status, spreadId, environmentName } = this.props.document;

    this.changeUnlockButtonStatus(true, false);

    api.spread
      .unlock(spreadId, environmentName, status)
      .catch(() => this.changeUnlockButtonStatus(false, false))
      .then(() => {
        this.changeUnlockButtonStatus(false, true);
        this.props.reloadBriefSpreads();
        setTimeout(() => this.changeUnlockButtonStatus(false, false), 5000);
      });
  };

  public UnlockedButton = () => (
    <div className="Documents__unlockSpread Documents__unlockSpread--unlocked newbuttonstyle">
      <span className="Documents__unlockSpread__icon">
        <UnlockedIcon />
      </span>
    </div>
  );

  public lockIcon = (locked: boolean, lockedBy: string) => {
    const { userId } = this.props.user;
    const { status } = this.props.document;
    const { isUnlockingByButton, isUnlockedByButton } = this.state;
    const isEnabled = !isDisabledState(status);

    if (isUnlockedByButton) {
      return this.UnlockedButton();
    }

    if (locked && lockedBy === userId && isEnabled) {
      return (
        <QuiqButton
          className="Documents__unlockSpread--locked newbuttonstyle"
          isLoading={isUnlockingByButton}
          onClick={this.unlockSpread}
          color="white"
        >
          <Tooltip
            classes={{ tooltip: "ToolTip-container" }}
            title="Unlock Spread"
            placement="top"
          >
            <span className="Documents__unlockSpread__icon">
              <LockIcon />
            </span>
          </Tooltip>
        </QuiqButton>
      );
    }

    if (locked) {
      return (
        <QuiqButton
          className="Documents__unlockSpread--locked newbuttonstyle"
          color="white"
        >
          <Tooltip
            classes={{ tooltip: "ToolTip-container" }}
            title={`Locked by ${lockedBy}`}
            placement="top"
          >
            <span className="Documents__unlockSpread__icon">
              <LockIcon />
            </span>
          </Tooltip>
        </QuiqButton>
      );
    }
    return "";
  };

  private isLockedBySomeoneElse = () => {
    const { locked, lockedBy } = this.props.document;
    const { userId } = this.props.user;

    return locked && lockedBy !== userId;
  };

  private onLatestPeriodMouseOver = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    this.setState({ latestPeriodElement: event.currentTarget });
  };

  private onLatestPeriodMouseOut = () => {
    this.setState({ latestPeriodElement: null });
  };

  private onEntityNameMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    this.setState({ entityNameEl: e.currentTarget });
  };

  private onEntityNameMouseOut = () => {
    this.setState({ entityNameEl: null });
  };

  private onModifiedByUserMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    this.setState({ modifiedByUserEl: e.currentTarget });
  };

  private onModifiedByUserMouseOut = () => {
    this.setState({ modifiedByUserEl: null });
  };

  private onModificationTimeMouseOver = (
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    this.setState({ modificationTimeEl: e.currentTarget });
  };

  private onModificationTimeMouseOut = () => {
    this.setState({ modificationTimeEl: null });
  };

  private onUploadedByUserMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    this.setState({ uploadedByUserEl: e.currentTarget });
  };

  private onUploadedByUserMouseOut = () => {
    this.setState({ uploadedByUserEl: null });
  };

  private onUploadTimeMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    this.setState({ uploadTimeEl: e.currentTarget });
  };

  private onUploadTimeMouseOut = () => {
    this.setState({ uploadTimeEl: null });
  };

  private renderPeriodCount = () => {
    let label: string = "";

    switch (Number(this.props.document.periodCount)) {
      case 1: {
        label = "One Period";
        break;
      }
      case 2: {
        label = "Two Periods";
        break;
      }
      default: {
        label = "No Periods";
        break;
      }
    }
    return label;
  };

  private renderFullText = el => {
    return (
      <Popper
        open={el.offsetWidth < el.scrollWidth}
        placement="top"
        anchorEl={el}
      >
        <div className={"Popper"}>{el.textContent}</div>
      </Popper>
    );
  };

  private updateComments = (comments: Comment[]) => {
    this.setState({ comments });
  };

  private markCommentsAsRead = () => {
    const { document, user } = this.props;
    const { comments } = this.state;

    if (!comments) {
      return;
    }

    const commentsMarkedAsRead = map((comment: Comment) => {
      if (comment.readBy && !contains(user.userId, comment.readBy)) {
        comment.readBy.push(user.userId);
      }
      return comment;
    }, comments);

    this.setState({
      comments: commentsMarkedAsRead,
      hasUnreadComments: false
    });

    api.comments.addUserToReadByForBriefspread(document.spreadId).then(() => {
      if (document.commentsReadBy) {
        document.commentsReadBy.push(user.userId);
        this.setState({
          hasUnreadComments: false
        });
      }
    });
  };

  private fetchComments = () => {
    this.setState({ areCommentsLoading: true });
    new Promise((resolve, reject) => {
      resolve(this.props.getComments(this.props.document.spreadId));
    }).then(() => {
      this.setState({ toggleCommentPopup: true });
    });
  };
}

export default connect(
  selectorsToProps({
    entity: getEntityViewState,
    comments: getComments
  }),
  {
    reloadBriefSpreads: reloadBriefSpreads.action,
    getComments: getCommentsAction.action
  }
)(DocumentsListRow);



// WEBPACK FOOTER //
// ./src/documentList/DocumentsListRow.tsx